import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './WindVectorField.css';

/**
 * Get color for wind speed (matching differential field colors)
 */
const getWindColor = (speed, maxSpeed = 25) => {
  const normalized = Math.min(speed / maxSpeed, 1);
  
  if (normalized < 0.25) return '#14b8a6'; // Teal (calm)
  if (normalized < 0.5) return '#3b82f6'; // Blue (moderate)
  if (normalized < 0.75) return '#8b5cf6'; // Purple (strong)
  return '#ec4899'; // Pink (very strong)
};

/**
 * Create a custom arrow icon for wind direction
 * Elegant design with glow effect
 */
const createWindArrowIcon = (direction, speed, maxSpeed = 25) => {
  // Calculate arrow length based on speed (normalized to maxSpeed)
  const normalizedSpeed = Math.min(speed / maxSpeed, 1);
  const arrowLength = 25 + (normalizedSpeed * 30); // 25-55 pixels
  
  // Get color based on speed
  const color = getWindColor(speed, maxSpeed);
  
  // Dynamic sizing
  const strokeWidth = 2.5 + (normalizedSpeed * 1.5);
  const headWidth = 8 + (normalizedSpeed * 4);
  const headLength = 10 + (normalizedSpeed * 5);
  
  // Create elegant SVG for arrow
  const svg = `
    <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="windGlow-${direction}-${speed}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="windGrad-${direction}-${speed}" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" style="stop-color:${color};stop-opacity:0.5" />
          <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
        </linearGradient>
      </defs>
      <g transform="translate(40,40) rotate(${direction})" filter="url(#windGlow-${direction}-${speed})">
        <!-- Glow -->
        <line x1="0" y1="3" x2="0" y2="-${arrowLength - headLength + 2}" 
              stroke="${color}" stroke-width="${strokeWidth + 4}" 
              stroke-linecap="round" opacity="0.3" />
        <!-- Main shaft -->
        <line x1="0" y1="3" x2="0" y2="-${arrowLength - headLength + 2}" 
              stroke="url(#windGrad-${direction}-${speed})" stroke-width="${strokeWidth}" 
              stroke-linecap="round" />
        <!-- Arrowhead -->
        <path d="M0,-${arrowLength} 
                 L-${headWidth},-${arrowLength - headLength} 
                 Q0,-${arrowLength - headLength * 0.5} ${headWidth},-${arrowLength - headLength} 
                 Z" 
              fill="${color}" />
        <!-- Base dot -->
        <circle cx="0" cy="3" r="${2 + normalizedSpeed}" fill="${color}" opacity="0.9" />
      </g>
    </svg>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'wind-vector-icon',
    iconSize: [80, 80],
    iconAnchor: [40, 40]
  });
};

/**
 * Generate wind direction from station data or simulate realistic patterns
 */
const getWindDirection = (station, index) => {
  // If wind direction exists in readings, use it (preferred)
  if (station.currentReadings?.windDirection !== undefined) {
    return station.currentReadings.windDirection;
  }
  
  // Otherwise, simulate realistic wind patterns as fallback
  // Create a gradient pattern (wind generally flows in a direction)
  // Use station coordinates to create a consistent pattern
  const baseDirection = (station.locationLat * 100 + station.locationLon * 100) % 360;
  const variation = (index * 17) % 30 - 15; // Add some variation
  return (baseDirection + variation + 360) % 360;
};

export function WindVectorField({ stations, visible = false }) {
  const map = useMap();
  const markersRef = useRef([]);

  useEffect(() => {
    if (!visible || !stations || stations.length === 0) {
      // Remove all markers
      markersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      markersRef.current = [];
      return;
    }

    // Find max wind speed for normalization
    const maxSpeed = Math.max(
      ...stations
        .filter(s => s.currentReadings?.windSpeed)
        .map(s => s.currentReadings.windSpeed),
      20 // Default max
    );

    // Create markers for each station with wind data
    const newMarkers = [];
    stations.forEach((station, index) => {
      if (!station.currentReadings || !station.isActive) return;
      
      const windSpeed = station.currentReadings.windSpeed;
      if (!windSpeed || windSpeed <= 0) return;

      const windDirection = getWindDirection(station, index);
      const icon = createWindArrowIcon(windDirection, windSpeed, maxSpeed);
      
      const marker = L.marker(
        [station.locationLat, station.locationLon],
        { icon, zIndexOffset: 1000 }
      );
      
      // Add popup with wind info
      const speedKmh = (windSpeed * 3.6).toFixed(1);
      marker.bindPopup(`
        <div class="wind-vector-popup">
          <strong>${station.stationCode}</strong><br/>
          Wind Speed: ${windSpeed.toFixed(1)} m/s (${speedKmh} km/h)<br/>
          Direction: ${Math.round(windDirection)}° ${getDirectionName(windDirection)}
        </div>
      `);
      
      marker.addTo(map);
      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;

    // Cleanup
    return () => {
      markersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      markersRef.current = [];
    };
  }, [map, stations, visible]);

  return null;
}

/**
 * Convert degrees to direction name
 */
function getDirectionName(degrees) {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 
                      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

